import type { AttemptOutcome, UnitState } from './loop2.ts';
/**
 * Keep tiers (BENCH-V3-PLAN §7.1, DESIGN ED-92 to ED-94): what the run keeps of a unit that has no accepted attempt.
 *
 *  - `strict` (ED-27, the default): the best eligible attempt (meaning checks and guard pass, R3 passes, S > 0). Unchanged.
 *  - `lenient`: an end-of-run pass, after Tier C, over the units that ended `failed`: the best attempt whose meaning checks and
 *    guard passed and that addressed or partly addressed a targeted channel is kept, even if R2, R3 or S failed. The operator
 *    sees each as `kept (lenient)` with its failure report and may revert it.
 *
 * Never kept under any tier: a failed meaning check (a changed number or date word, a lost name, an altered quotation or
 * citation, a new URL, e-mail address, tag or fence, a paragraph-count mismatch, a word ratio out of range, an empty
 * rewrite), a failed dialogue guard, a structure error, declined, truncated and schema failures. `permissive` (deferred)
 * would relax the word ratio alone. The classification below is exhaustive over the meaning checks: a new check must be
 * classified before the code compiles.
 */
import type { MeaningCheckId } from './meaning2.ts';

export type KeepTier = 'strict' | 'lenient';
export const KEEP_TIERS: readonly KeepTier[] = ['strict', 'lenient'];
export const DEFAULT_KEEP: KeepTier = 'strict';

/** Every meaning check is a reason never to keep a version; the sentence says what it protects. */
export const NEVER_KEPT: Readonly<Record<MeaningCheckId, string>> = {
	empty: 'an empty rewrite',
	'word-ratio': 'a rewrite much longer or shorter than the passage',
	numbers: 'a changed number',
	'date-words': 'a changed month or weekday',
	names: 'a lost name',
	citations: 'an altered citation',
	'dialogue-untargeted': 'an altered quotation that was not flagged',
	'quote-count': 'a changed number of quotations',
	'quote-delimiters': 'changed quotation marks',
	'quote-balance': 'unbalanced quotation marks',
	'new-url': 'a new URL',
	'new-email': 'a new e-mail address',
	'new-tag': 'a new HTML tag',
	'new-fence': 'a new code fence',
	separators: 'a changed number of paragraphs',
	// the guardrails' distinction checks (GR3)
	negation: 'a changed negation or its scope',
	modality: 'a changed modal verb',
	certainty: 'a changed estimate or doubt',
	temporal: 'a changed time scope',
	attribution: 'a changed source of knowledge',
};

/** What the lenient pass needs of an attempt's record. */
export interface LenientFacts {
	outcome: AttemptOutcome;
	meaningOk: boolean;
	guardOk: boolean;
	addressed: number;
	partial: number;
}

/**
 * A lenient candidate: measured and rejected (declined, truncated, schema and held attempts have no usable text), the
 * meaning checks and the guard passed (a structure error is recorded as a failed meaning check), and a targeted channel is
 * addressed or partly addressed.
 */
export const isLenientCandidate = (a: LenientFacts): boolean =>
	a.outcome === 'rejected' && a.meaningOk && a.guardOk && a.addressed + a.partial > 0;

/** A candidate as re-measured on the final table. */
export interface LenientMeasured {
	n: number;
	addressed: number;
	/** S under the `S` rule, else the net removal. */
	score: number;
	/** Channels whose document score rose past their margin. */
	rises: number;
	/** Word-level edit distance from the unit's original. */
	distance: number;
}

/**
 * The lenient rank, best first: more channels addressed, a higher S (the net removal under the `events` rule), fewer document
 * rises, a smaller edit distance, then the later attempt (a total order, so the same records give the same keep).
 */
export function rankLenient<T extends LenientMeasured>(xs: readonly T[]): T[] {
	return xs
		.slice()
		.sort(
			(a, b) =>
				b.addressed - a.addressed ||
				b.score - a.score ||
				a.rises - b.rises ||
				a.distance - b.distance ||
				b.n - a.n
		);
}

/* ---------------------------------------------------------------- ED-66's accounting */

export interface UnitBar {
	/** Units a rewrite was tried on (not `not-selected`, not `pending`, not `declined`). */
	attempted: number;
	/** Final status `failed`, kept-best units aside. */
	failed: number;
	/** Strict keeps (ED-27): flagged to the customer, not counted for the bar. */
	keptStrict: number;
	/** Lenient keeps: failures by the standard, so counted for the bar and reported separately. */
	keptLenient: number;
	/** `failed + keptLenient`: the numerator of ED-66's bar. */
	barFailed: number;
	/**
	 * Units the guardrails' editor left as they are (GR2, ED-194): outside the bar's numerator and denominator, reported
	 * apart. Absent when there is none (always without the guardrails).
	 */
	declined?: number;
	/** One-sided 95% upper bound of `barFailed / attempted` (Clopper-Pearson); the bar is 0.30. */
	upper95: number;
}

/** One-sided 95% upper bound of a binomial proportion, by bisection on the exact cdf. */
export function upper95(k: number, n: number): number {
	if (n === 0 || k >= n) return 1;
	const cdf = (p: number): number => {
		let s = 0;
		let lc = 0;
		for (let i = 0; i <= k; i++) {
			if (i > 0) lc += Math.log((n - i + 1) / i);
			s += Math.exp(lc + i * Math.log(p) + (n - i) * Math.log(1 - p));
		}
		return s;
	};
	let lo = k / n;
	let hi = 1;
	for (let i = 0; i < 80; i++) {
		const mid = (lo + hi) / 2;
		if (cdf(mid) > 0.05) lo = mid;
		else hi = mid;
	}
	return (lo + hi) / 2;
}

/** ED-66's unit-failure accounting with lenient keeps counted as failed (BENCH-V3-PLAN §7.1). */
export function unitBar(states: readonly (UnitState | undefined)[]): UnitBar {
	let attempted = 0;
	let failed = 0;
	let keptStrict = 0;
	let keptLenient = 0;
	let declined = 0;
	for (const s of states) {
		if (!s || s.status === 'not-selected' || s.status === 'pending') continue;
		if (s.status === 'declined') {
			declined++;
			continue;
		}
		attempted++;
		if (s.keptLenient) keptLenient++;
		else if (s.keptBest) keptStrict++;
		else if (s.status === 'failed') failed++;
	}
	const barFailed = failed + keptLenient;
	return {
		attempted,
		failed,
		keptStrict,
		keptLenient,
		barFailed,
		...(declined > 0 ? { declined } : {}),
		upper95: upper95(barFailed, attempted),
	};
}
